import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRouterState } from '@tanstack/react-router';
import { List, RotateCcw, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { trpcClient } from '@/lib/trpc';
import { cn } from '@/lib/utils';
import { emsTabClass } from '@/components/ui/ems-tabs';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';

/**
 * Tab "Thùng rác" chuẩn cho mọi trang danh sách (ListPageShell `trashModule`):
 * liệt kê bản ghi đã xoá của chính trang đó, tìm theo ô tìm kiếm chung trên header, khôi phục từng bản ghi.
 */

type DeletedRow = { id: string; code: string; text: string | null; deletedAt: string | Date };

// Tab chuẩn EMS (thẻ + gạch chân đậm), cỡ gọn cho thanh công cụ danh sách
const tabClass = (active: boolean, tone: 'primary' | 'rose') => emsTabClass(active, { size: 'sm', tone });

export function useDeletedCount(module?: string) {
    return useQuery({
        queryKey: ['trash-deleted', module, 'count'],
        queryFn: async () => (await trpcClient.trash.listDeleted.query({ module: module!, page: 1, limit: 1 })).meta.total as number,
        enabled: !!module,
        staleTime: 30_000,
        retry: false,
    });
}

/** Dải tab: tab của trang (hoặc "Danh sách") + "Thùng rác (n)" */
export function TrashTabs({
    showTrash,
    onChange,
    pageTabs,
    count,
}: {
    showTrash: boolean;
    onChange: (trash: boolean) => void;
    pageTabs?: React.ReactNode;
    count?: number;
}) {
    return (
        <div className="flex items-center gap-3">
            {showTrash || !pageTabs ? (
                <button type="button" data-testid="list-tab-active" className={tabClass(!showTrash, 'primary')} onClick={() => onChange(false)}>
                    <List className="w-3.5 h-3.5" />
                    <span>Danh sách</span>
                </button>
            ) : (
                pageTabs
            )}
            <div className="h-4 w-px bg-slate-200 shrink-0" />
            <button type="button" data-testid="list-tab-trash" className={tabClass(showTrash, 'rose')} onClick={() => onChange(true)}>
                <Trash2 className="w-3.5 h-3.5 text-rose-500" />
                <span>Thùng rác</span>
                {typeof count === 'number' && (
                    <span className={cn('px-1.5 rounded-full text-[10px] font-bold tabular-nums', showTrash ? 'bg-rose-100 text-rose-700' : 'bg-slate-200 text-slate-600')}>
                        {count}
                    </span>
                )}
            </button>
        </div>
    );
}

/** Bảng bản ghi đã xoá + phân trang riêng */
export function DeletedRecordsTable({ module, unitLabel }: { module: string; unitLabel?: string }) {
    const queryClient = useQueryClient();
    const search: string = useRouterState({ select: (s) => String((s.location.search as any)?.q || '') });
    const [page, setPage] = useState(1);
    const [confirm, setConfirm] = useState<DeletedRow | null>(null);
    const limit = 25;

    const { data, isLoading } = useQuery({
        queryKey: ['trash-deleted', module, page, search],
        queryFn: () => trpcClient.trash.listDeleted.query({ module, page, limit, search: search || undefined }),
    });
    const restore = useMutation({
        mutationFn: (id: string) => trpcClient.trash.restoreDeleted.mutate({ module, id }),
        onSuccess: () => {
            toast.success('Đã phục hồi bản ghi');
            queryClient.invalidateQueries({ queryKey: ['trash-deleted', module] });
            queryClient.invalidateQueries();
        },
        onError: (e: any) => toast.error(e?.message || 'Không phục hồi được bản ghi'),
    });

    const rows: DeletedRow[] = (data?.items as DeletedRow[]) || [];
    const total = data?.meta.total || 0;
    const totalPages = data?.meta.totalPages || 1;

    return (
        <div className="flex flex-col min-h-full" data-testid="deleted-records">
            <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600">
                    <tr className="whitespace-nowrap">
                        <th className="text-left font-bold px-3 py-2 w-[30%]">Mã</th>
                        <th className="text-left font-bold px-3 py-2">Mô tả</th>
                        <th className="text-left font-bold px-3 py-2 w-[160px]">Ngày xoá</th>
                        <th className="text-right font-bold px-3 py-2 w-[120px]">Thao tác</th>
                    </tr>
                </thead>
                <tbody>
                    {isLoading && (
                        <tr><td colSpan={4} className="px-3 py-8 text-center text-slate-400">Đang tải...</td></tr>
                    )}
                    {!isLoading && rows.length === 0 && (
                        <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-400">Thùng rác trống</td></tr>
                    )}
                    {rows.map((r) => (
                        <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid="deleted-row">
                            <td className="px-3 py-2 font-semibold text-slate-800">{r.code}</td>
                            <td className="px-3 py-2 text-slate-600">{r.text || '—'}</td>
                            <td className="px-3 py-2 text-slate-500 tabular-nums">
                                {new Date(r.deletedAt).toLocaleString('vi-VN', { timeZone: 'Asia/Ho_Chi_Minh', dateStyle: 'short', timeStyle: 'short' })}
                            </td>
                            <td className="px-3 py-2 text-right">
                                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" disabled={restore.isPending} onClick={() => setConfirm(r)}>
                                    <RotateCcw className="w-3.5 h-3.5" /> Phục hồi
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {totalPages > 1 && (
                <div className="mt-auto flex items-center justify-between px-3 py-2 border-t border-slate-200 bg-white text-xs text-slate-600">
                    <span>{total} {unitLabel || 'bản ghi'} đã xoá</span>
                    <div className="flex items-center gap-2">
                        <Button size="sm" variant="outline" className="h-7" disabled={page <= 1} onClick={() => setPage(page - 1)}>Trước</Button>
                        <span className="tabular-nums">{page}/{totalPages}</span>
                        <Button size="sm" variant="outline" className="h-7" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Sau</Button>
                    </div>
                </div>
            )}
            <ConfirmDialog
                open={!!confirm}
                onOpenChange={(open) => !open && setConfirm(null)}
                title="Phục hồi bản ghi?"
                description={confirm ? `Phục hồi ${confirm.code} về danh sách đang dùng.` : ''}
                confirmText="Phục hồi"
                onConfirm={() => {
                    if (confirm) restore.mutate(confirm.id);
                    setConfirm(null);
                }}
            />
        </div>
    );
}
